import { AxiosError } from 'axios';
import { createStore, useStore } from 'zustand';

import { authStore } from '@stores/auth';
import { fetchOrdersDriversInfo } from '@lib/api/fetchDriverInfo';
import { logger } from '@lib/logger';
import { lookupsStore } from '@stores/lookups';
import { FirestoreOrderSummary, OrderSummary } from '@lib/types';
import { Unsubscribe, firebase } from '@lib/firebase';

import { fromFirestoreOrderSummaryToOrderSummary } from './fromFirestoreOrderSummaryToOrderSummary';
import { fromTripsDriversToOrdersSummary } from './utils/fromTripsDriversToOrdersSummary';

interface AcceptedOrdersStore {
  loading: boolean;
  error: Error | null;

  orders: OrderSummary[];
  fetch: () => Promise<void>;
  subscribe: () => void;
  unsubscribe: () => void;
  onSnapshotUnsubscribe: Unsubscribe | null;
  accept: (order: OrderSummary) => void;
}

export const acceptedOrdersStore = createStore<AcceptedOrdersStore>(
  (set, get) => ({
    error: null,
    loading: false,
    orders: [],
    onSnapshotUnsubscribe: null,
    subscribe: () => {
      const lookups = lookupsStore.getState().value;
      
      // 🟢 GUARD 1: Don't subscribe if lookups aren't ready
      if (!lookups || !lookups.query) {
        return;
      }
      
      // unsubscribe first
      get().onSnapshotUnsubscribe?.();

      set({ loading: true });
      const onSnapshotUnsubscribe =
        firebase.onMultipleQueriesSnapshot<FirestoreOrderSummary>({
          collection: 'ordersSummary',
          queries: [
            {
              where: [
                ['state', '==', 'ACCEPTED'],
                lookupsStore.getState().value?.query ?? null,
              ],
              order: [['createdAt', 'asc']],
            },
            {
              where: [
                ['state', '==', 'FOOD_IS_READY'],
                lookupsStore.getState().value?.query ?? null,
              ],
              order: [['createdAt', 'asc']],
            },
          ],
          onNext: async (firestoreOrders) => {
            const orders = firestoreOrders.map(
              fromFirestoreOrderSummaryToOrderSummary,
            );

            const drivers = await fetchOrdersDriversInfo(orders);
            const newOrders = await fromTripsDriversToOrdersSummary(
              drivers,
              orders,
            );

            set({
              loading: false,
              error: null,
              orders: newOrders,
            });
          },
          onError: (error) => {
            console.error('Error occurred while fetching orders:', error);
            get().subscribe();
            set({ error, loading: false });
            logger.error(error);
          },
          sortPredicate: (item) => item.createdAt.toDate().getTime(),
        });

      set({ onSnapshotUnsubscribe });
    },
    fetch: async () => {
      try {
        set({ loading: true });
        let orders = get().orders;
        const drivers = await fetchOrdersDriversInfo(orders);
        orders = await fromTripsDriversToOrdersSummary(drivers, orders);
        set({ orders });
      } catch (error) {
        logger.error(error);
        set({ error: error as AxiosError });
      } finally {
        set({ loading: false });
      }
    },
    unsubscribe: () => {
      get().onSnapshotUnsubscribe?.();
      set({ onSnapshotUnsubscribe: null, orders: [] });
    },
    accept: () => {},
  }),
);

function onLookupsReady() {
  if (!lookupsStore.getState().value) return;

  acceptedOrdersStore.getState().subscribe();
}

// future changes
lookupsStore.subscribe((state, prevState) => {
  if (!prevState?.value && state.value) {
    onLookupsReady();
  }
});

// initial state (FIXES ANDROID)
if (lookupsStore.getState().value) {
  onLookupsReady();
}

// clear out orders and unsubscribe on logout
authStore.subscribe((state) => {
  if (acceptedOrdersStore.getState().orders && !state.computed.isLoggedIn) {
    acceptedOrdersStore.getState().unsubscribe();
  }
});

export const useAcceptedOrdersStore = () => useStore(acceptedOrdersStore);
export const useAcceptedOrder = (id: string) => {
  const { orders } = useAcceptedOrdersStore();
  return { order: orders.find((o) => o.uuid === id) } as const;
};
